import { CloseIcon, DeleteIcon } from 'icons'
import { Component, createEffect, on, Show } from 'solid-js'
import { createStore } from 'solid-js/store'
import { deepcopy } from 'utils/tools'

import './style/datePicker.scss'

interface DatePickerProps {
    show: boolean
    current: string
    onClose(): void
    onSave(date: string): void
    onClear(): void
}
const DatePicker: Component<DatePickerProps> = P => {
    let secRef: HTMLInputElement
    let minRef: HTMLInputElement
    let hourRef: HTMLInputElement

    type DATA = {
        timestamp: {
            day: number
            mounth: number
            year: number
        }
    }
    const DATA_DEFAULT: DATA = {
        timestamp: {
            day: -1,
            mounth: -1,
            year: -1,
        },
    }

    const [data, setData] = createStore<DATA>(deepcopy(DATA_DEFAULT))

    const close = () => {
        P.onClose()
        setData(deepcopy(DATA_DEFAULT))
    }

    createEffect(
        on(
            () => P.show,
            s => {
                if (!s || !P.current) return setData(deepcopy(DATA_DEFAULT))

                let times = deepcopy(P.current).split('/')
                const [year, mounth, day] = times

                if (!day || !mounth || !year)
                    return setData(deepcopy(DATA_DEFAULT))

                setData('timestamp', {
                    year: parseInt(year),
                    mounth: parseInt(mounth),
                    day: parseInt(day),
                })
            }
        )
    )

    const save = () => {
        if (!P.show) return

        let s = deepcopy(data.timestamp)
        let day = s.day || 1
        let mounth = s.mounth || 1
        let year = s.year || 1

        let date = year.toString() + '/'
        if (mounth == -1) date += '%'
        else date += mounth.toString().padStart(2, '0')

        date += '/'

        if (day == -1) date += '%'
        else date += day.toString().padStart(2, '0')

        // const date = `${year}/${String(mounth).padStart(2, '0')}/${String(day).padStart(2, '0')}`

        P.onSave(date)
    }

    function setTimeField<K extends keyof DATA['timestamp']>(
        field: K,
        raw: string
    ) {
        // raw is the input.value (string). If empty -> sentinel -1.
        if (raw.trim() === '') {
            // set sentinel -1
            setData('timestamp', field, -1)
            return
        }
        // parse int, ignore non-numeric
        const n = parseInt(raw.replace(/[^\d-]/g, ''), 10)
        if (Number.isNaN(n)) {
            // don't change on invalid parse
            return
        }

        if (field === 'year') {
            const val = Math.max(0, n) // hour >= 0
            setData('timestamp', 'year', val)
            // if minutes/seconds were -1 set them to 0 for convenience
            if (data.timestamp.mounth === -1) setData('timestamp', 'mounth', 1)
            if (data.timestamp.day === -1) setData('timestamp', 'day', 1)
        } else if (field == 'mounth') {
            // min or sec -> clamp 0..59
            const clamped = Math.max(0, Math.min(12, n))
            setData('timestamp', field, clamped)
            // if hour was -1 (unset) set hour to 0 to make a valid time
            if (data.timestamp.year === -1) setData('timestamp', 'year', 0)
        } else if (field == 'day') {
            const clamped = Math.max(0, Math.min(31, n))
            setData('timestamp', field, clamped)
        }
    }

    // optional: onBlur ensure clamping & empty -> -1 logic
    function onTimeBlur<K extends keyof DATA['timestamp']>(field: K) {
        const cur = data.timestamp[field]
        if (field === 'year') {
            if (cur === -1) return
            setData('timestamp', 'year', Math.max(0, Math.floor(cur)))
        } else {
            if (cur === -1) return
            setData(
                'timestamp',
                field,
                Math.max(0, Math.min(59, Math.floor(cur)))
            )
        }
    }

    const TimestampInputs: Component = () => {
        // helper for displaying value: show "" when -1
        const display = (v: number) => (v === -1 ? '' : String(v))

        return (
            <div class='time-fields'>
                {/* Second */}
                <label class='time-field'>
                    <span class='title_small'>روز</span>
                    <input
                        ref={e => (secRef = e)}
                        class='title_hero'
                        type='number'
                        inputmode='numeric'
                        maxlength={2}
                        min='0'
                        max='31'
                        value={display(data.timestamp.day)}
                        onclick={() => secRef.select()}
                        onInput={e => {
                            if (e.currentTarget.value.length >= 2) {
                                minRef.focus()
                                minRef.select()
                            }
                            if (e.currentTarget.value.length > 2) {
                                e.currentTarget.value = String(
                                    data.timestamp.day
                                )

                                return
                            }

                            setTimeField(
                                'day',
                                (e.target as HTMLInputElement).value
                            )
                        }}
                        onBlur={() => onTimeBlur('day')}
                    />
                </label>
                <span class='divider title_hero'>/</span>

                {/* Minute */}
                <label class='time-field'>
                    <span class='title_small'>ماه</span>
                    <input
                        ref={e => (minRef = e)}
                        class='title_hero'
                        type='number'
                        inputmode='numeric'
                        maxlength={2}
                        min='0'
                        max='12'
                        value={display(data.timestamp.mounth)}
                        onclick={() => minRef.select()}
                        onInput={e => {
                            if (e.currentTarget.value.length >= 2) {
                                hourRef.focus()
                                hourRef.select()
                            }
                            if (e.currentTarget.value.length > 2) {
                                e.currentTarget.value = String(
                                    data.timestamp.mounth
                                )

                                return
                            }

                            setTimeField(
                                'mounth',
                                (e.target as HTMLInputElement).value
                            )
                        }}
                        onBlur={() => onTimeBlur('mounth')}
                    />
                </label>

                <span class='divider title_hero'>/</span>

                {/* Hour */}
                <label class='time-field'>
                    <span class='title_small'>سال</span>
                    <input
                        ref={e => (hourRef = e)}
                        class='title_hero'
                        type='number'
                        inputmode='numeric'
                        maxlength={4}
                        min='0'
                        value={display(data.timestamp.year)}
                        onclick={() => hourRef.select()}
                        onInput={e => {
                            if (e.currentTarget.value.length > 4) {
                                e.currentTarget.value = String(
                                    data.timestamp.year
                                )

                                return
                            }

                            setTimeField('year', e.currentTarget.value)
                        }}
                        onBlur={() => onTimeBlur('year')}
                    />
                </label>
            </div>
        )
    }

    return (
        <div
            class='date-picker-cmp'
            classList={{ show: P.show }}
            onMouseDown={close}
            ontouchstart={close}
        >
            <form
                onsubmit={e => {
                    e.preventDefault()

                    save()
                }}
                class='popup-wrapper'
                onMouseDown={e => e.stopPropagation()}
                ontouchstart={e => e.stopPropagation()}
            >
                <div class='scaler'></div>
                <div class='card'>
                    <div class='card-head'>
                        <p class='sub title_smaller'>انتخاب تاریخ</p>
                    </div>
                    <div class='card-inps'>
                        <TimestampInputs />

                        <Show when={P.current}>
                            <button
                                class='clear-cmp-date title_smaller'
                                type='button'
                                onclick={P.onClear}
                            >
                                <DeleteIcon />
                                حذف تاریخ
                            </button>
                        </Show>
                    </div>

                    <div class='card-cta'>
                        <button class='title_smaller'>تایید</button>
                    </div>
                </div>
                <div class='close icon' role='button' onclick={close}>
                    <CloseIcon />
                </div>
            </form>
        </div>
    )
}

export { DatePicker }
